import { useState } from 'react'
import { useNavigate } from 'react-router'
import { App as AntdApp, Button, Card, Collapse, Descriptions, Divider, Form, Input, Space, Typography } from 'antd'
import { ControlOutlined } from '@ant-design/icons'
import { changePassword } from '@/api/endpoints/auth'
import { presentError } from '@/api/presentError'
import { setAccessToken } from '@/api/tokenStore'
import SponsorBanner from '@/components/SponsorBanner'
import { requestReplay } from '@/features/guide/guideIds'
import { zh } from '@/locales/zh-CN'
import { paths } from '@/routes/paths'
import { useAuthStore } from '@/stores/auth.store'

interface FormValues {
  current_password: string
  new_password: string
  confirm: string
}

const PASSWORD_MIN = 8

export default function ProfilePage() {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()
  const { message, modal } = AntdApp.useApp()
  const t = zh.auth.profile

  const [form] = Form.useForm<FormValues>()
  const [submitting, setSubmitting] = useState(false)

  const onChangePassword = async (values: FormValues) => {
    setSubmitting(true)
    try {
      const response = await changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      })
      // 改密会撤销其它设备上的全部会话，并为当前设备补发一套新凭证 ——
      // 不存下来的话，用户会在下一次请求时被登出
      setAccessToken(response.access_token, response.expires_in)
      form.resetFields()
      message.success(t.passwordChanged)
    } catch (caught) {
      const presented = presentError(caught)
      if (presented.fields.length > 0) {
        form.setFields(
          presented.fields.map((f) => ({ name: f.field as keyof FormValues, errors: [f.message] })),
        )
      } else {
        message.error(presented.text)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const onLogout = () => {
    modal.confirm({
      title: t.logoutConfirmTitle,
      okText: t.logoutOk,
      cancelText: t.logoutCancel,
      onOk: async () => {
        await logout()
        navigate(paths.login, { replace: true })
      },
    })
  }

  return (
    <div className="page page--readable">
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        {t.title}
      </Typography.Title>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={1} size="small" colon={false}>
          <Descriptions.Item label={t.name}>{user?.name ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t.studentId}>{user?.student_id ?? '—'}</Descriptions.Item>
        </Descriptions>
        {/* §7.1：首次激活后学号与姓名不能自行修改 */}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t.nameLockNotice}
        </Typography.Text>
      </Card>

      {/*
        §5：审核员与超管也可以同时是参赛者。给他们一个回后台的入口，
        否则既是审核员又要给自己打卡的人只能手敲 URL。
        后端才是真正的权限边界，这里只决定要不要渲染入口。
      */}
      {(user?.role === 'reviewer' || user?.role === 'super_admin') && (
        <Button block icon={<ControlOutlined />} style={{ marginBottom: 16 }} onClick={() => navigate(paths.admin.dashboard)}>
          {zh.admin.enterAdmin}
        </Button>
      )}

      {/*
        操作指导每个浏览器只演示一遍，误关了或想复习就只能从这里进。
        点击先记下重播请求、再跳到目标页面，由那个页面的 GuideTour 播放 ——
        目标是另一个组件，所以中间经手的是 guideIds 里的模块变量。
      */}
      <Card title={t.guide} size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Button
            block
            onClick={() => {
              requestReplay('home')
              navigate(paths.home)
            }}
          >
            {t.guideCheckin}
          </Button>
          <Button
            block
            onClick={() => {
              requestReplay('leaderboard')
              navigate(paths.leaderboard)
            }}
          >
            {t.guideLeaderboard}
          </Button>
        </Space>
      </Card>

      {/*
        修改密码平时用不到，三个输入框常驻只会把页面拖长，折叠成一行表头。
        Collapse 的表头自带 role="button" 与 aria-expanded，无障碍不必自己补；
        不传 defaultActiveKey，默认收起（与「提交历史」「历史版本」一致）。
      */}
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: 'change-password',
            label: t.changePassword,
            children: (
              <Form<FormValues> form={form} layout="vertical" onFinish={onChangePassword} requiredMark={false}>
                <Form.Item
                  name="current_password"
                  label={t.currentPassword}
                  rules={[{ required: true, message: t.currentPasswordRequired }]}
                >
                  <Input.Password autoComplete="current-password" />
                </Form.Item>

                <Form.Item
                  name="new_password"
                  label={t.newPassword}
                  rules={[
                    { required: true, message: t.newPasswordRequired },
                    { min: PASSWORD_MIN, message: t.newPasswordMin(PASSWORD_MIN) },
                    { pattern: /[A-Za-z]/, message: t.newPasswordNeedsLetter },
                    { pattern: /\d/, message: t.newPasswordNeedsDigit },
                  ]}
                >
                  <Input.Password autoComplete="new-password" placeholder={t.newPasswordPlaceholder} />
                </Form.Item>

                <Form.Item
                  name="confirm"
                  label={t.confirmNewPassword}
                  dependencies={['new_password']}
                  rules={[
                    { required: true, message: t.confirmRequired },
                    ({ getFieldValue }) => ({
                      validator: (_rule, value) =>
                        !value || getFieldValue('new_password') === value
                          ? Promise.resolve()
                          : Promise.reject(new Error(t.confirmMismatch)),
                    }),
                  ]}
                >
                  <Input.Password autoComplete="new-password" />
                </Form.Item>

                <Button type="primary" htmlType="submit" block loading={submitting}>
                  {t.savePassword}
                </Button>
              </Form>
            ),
          },
        ]}
      />

      <Divider />

      <Button danger block onClick={onLogout} style={{ marginBottom: 24 }}>
        {t.logout}
      </Button>

      <SponsorBanner />
    </div>
  )
}
